import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useData } from '../../context/DataContext';
import { useLanguage } from '../../context/LanguageContext';
import { TRADER_LIST, isFenceQuestVisible, isQuestUnlocked } from '../../utils/constants';
import { getLanguageFromPath, localizedPath } from '../../utils/routes';

import DashboardStats from './DashboardStats';
import ActiveQuestsList from './ActiveQuestsList';
import TraderLoyaltyPanel from './TraderLoyaltyPanel';

function Dashboard({ profile, onUpdate }) {
  const { quests = [], traders = [], kappaItems = [], isLoaded } = useData();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [sortBy, setSortBy] = useState('none');
  const [expandedQuest, setExpandedQuest] = useState(null);

  const dashboardData = useMemo(() => {
    if (!isLoaded || !quests || quests.length === 0 || !profile) return null;

    const completedIds = profile.completedQuests || [];
    const collectedItemIds = profile.kappaItems || [];

    // --- İstatistik Hesaplamaları ---
    const kappaQuests = quests.filter(q => q.kappaRequired);
    const hideoutTotalPoints = 68;
    const hideoutCurrentPoints = Object.values(profile.hideoutModules || {}).reduce((acc, lvl) => acc + lvl, 0);

    // --- Filtreleme Mantığı ---
    const available = quests.filter(q => {
      if (completedIds.includes(q.id)) return false;
      if (!isFenceQuestVisible(q, profile.fenceRep || 0)) return false;
      return isQuestUnlocked(q, completedIds, profile.level || 1);
    });

    let filteredQuests = available;
    if (sortBy.startsWith('trader:')) {
      const selectedTrader = sortBy.replace('trader:', '');
      filteredQuests = available.filter(q => q.trader?.name === selectedTrader);
    } else if (sortBy.startsWith('map:')) {
      const selectedMap = sortBy.replace('map:', '');
      filteredQuests = available.filter(q => (q.mapName === 'Multiple' ? t('multiple') : q.mapName) === selectedMap);
    }

    const validTraders = traders
      .filter(t => TRADER_LIST.includes(t.normalizedName || t.name.toLowerCase()))
      .sort((a, b) => TRADER_LIST.indexOf(a.normalizedName) - TRADER_LIST.indexOf(b.normalizedName));

    return {
      stats: {
        quests: { total: quests.length, completed: completedIds.length },
        kappa: { total: kappaQuests.length, completed: kappaQuests.filter(q => completedIds.includes(q.id)).length },
        hideout: { total: hideoutTotalPoints, current: hideoutCurrentPoints },
        kappaItems: { total: kappaItems.length || 39, collected: collectedItemIds.length }
      },
      activeQuests: filteredQuests,
      validTraders,
      allMaps: Array.from(new Set(quests.map(q => q.mapName === 'Multiple' ? t('multiple') : q.mapName))).sort()
    };
  }, [quests, traders, kappaItems, profile, sortBy, isLoaded, t]);

  if (!isLoaded || !dashboardData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="text-emerald-500 font-mono text-xl animate-pulse flex items-center gap-2">
          <div className="w-3 h-3 bg-emerald-500 rounded-full animate-bounce"></div>
          {t('loading_dashboard') || 'Loading Dashboard...'}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12 bg-transparent">
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <DashboardStats
          stats={dashboardData.stats}
          onNavigate={(path) => navigate(localizedPath(path, getLanguageFromPath(window.location.pathname) || language))}
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <ActiveQuestsList
            quests={dashboardData.activeQuests}
            allMaps={dashboardData.allMaps}
            validTraders={dashboardData.validTraders}
            sortBy={sortBy}
            setSortBy={setSortBy}
            expandedQuest={expandedQuest}
            setExpandedQuest={setExpandedQuest}
            profile={profile}
            onUpdate={onUpdate}
          />
          <TraderLoyaltyPanel
            traders={dashboardData.validTraders}
            profile={profile}
            onUpdate={onUpdate}
          />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
